import { useCallback, useEffect, useState } from "react";
import { projectionMonitoringApi } from "@/features/observability/api/projection-monitoring.api";
import {
    ProjectionHealth,
    LagDistribution,
    DLQDepth,
    DLQEventsOutput,
    DriftReportDoc,
    ReconcileSummary,
} from "@/features/observability/types/projection.types";

const REFRESH_MS = 30_000;

export type ProjectionMonitoringData = {
    health: ProjectionHealth | null;
    lag: LagDistribution | null;
    dlq: DLQDepth | null;
    dlqEvents: DLQEventsOutput | null;
    drift: DriftReportDoc[] | null;
};

const EMPTY: ProjectionMonitoringData = {
    health: null,
    lag: null,
    dlq: null,
    dlqEvents: null,
    drift: null,
};

const valueOrNull = <T>(result: PromiseSettledResult<T>): T | null =>
    result.status === "fulfilled" ? result.value : null;

export function useProjectionMonitoring() {
    const [data, setData] = useState<ProjectionMonitoringData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        // Effect-scoped (not a ref): prevents interval ticks from stacking under
        // a slow cluster, without surviving StrictMode's mount/unmount/mount and
        // wedging the initial load.
        let running = false;

        const load = async () => {
            if (running) return;
            running = true;
            setLoading(true);
            // allSettled, not all: one failing endpoint must not blank the whole
            // board. Each dataset degrades independently; last-known values stay.
            const [health, lag, dlq, dlqEvents, drift] = await Promise.allSettled([
                projectionMonitoringApi.fetchHealth(),
                projectionMonitoringApi.fetchLag(),
                projectionMonitoringApi.fetchDLQ(),
                projectionMonitoringApi.fetchDLQEvents(),
                projectionMonitoringApi.fetchDrift(),
            ]);
            running = false;
            if (cancelled) return;

            setData((prev) => ({
                health: valueOrNull(health) ?? prev.health,
                lag: valueOrNull(lag) ?? prev.lag,
                dlq: valueOrNull(dlq) ?? prev.dlq,
                dlqEvents: valueOrNull(dlqEvents) ?? prev.dlqEvents,
                drift: valueOrNull(drift) ?? prev.drift,
            }));
            // Health is the core signal: only a health failure is a hard error.
            if (health.status === "fulfilled") {
                setError(false);
                setLastUpdatedAt(Date.now());
            } else {
                setError(true);
            }
            setLoading(false);
        };

        void load();
        const intervalId = setInterval(() => void load(), REFRESH_MS);
        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, [reloadKey]);

    const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

    const reconcile = useCallback(
        async (limit: number): Promise<ReconcileSummary> => {
            const summary = await projectionMonitoringApi.reconcileDLQ(limit);
            setReloadKey((key) => key + 1);
            return summary;
        },
        []
    );

    return { data, loading, error, lastUpdatedAt, refresh, reconcile };
}
